import { type JSX, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { AdvancedFeatureUnavailableExplanation } from 'Components/AdvancedFeatureUnavailableExplanation/AdvancedFeatureUnavailableExplanation';
import { GenerativeAiDisclaimer } from 'Components/ArtificialIntelligence/GenerativeAiDisclaimer';
import { Button } from 'Components/Buttons/Buttons';
import { Switch } from 'Components/Buttons/Switch';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { ICON_CLOSE, ICON_SUBMIT } from 'Config/Icons';
import { getSummitGuideDueDiligenceFeature } from 'Helpers/UcrmTprm';
import { AiDueDiligenceAnalyzeAssessmentRequest } from 'Models/ArtificialIntelligence';
import { AssessmentSubjectArchiveBase, AssessmentSubjectBase } from 'Models/AssessmentSubject';
import { ClientDetailsModel } from 'Models/ClientDetailsModel';
import { ValidationError } from 'Models/ErrorTypes';
import { UploadedFile } from 'Models/Files';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';

import styles from './DueDiligenceAiAnalysisModal.module.css';

enum FormState {
    None,
    Submitting,
    Submitted,
}

interface AdvertiseDueDiligenceAiAnalysisModalProps {
    hideModal: () => void;
    module: DueDiligenceModule;
    reasonUnavailable: 'notGranted' | 'grantedButDisabled';
}

/**
 * This informational modal is used when the Client does not have the module's SummIT Guide™ due diligence feature (see `getSummitGuideDueDiligenceFeature`). It acts as an advertisement for the feature.
 */
export const AdvertiseDueDiligenceAiAnalysisModal = (props: AdvertiseDueDiligenceAiAnalysisModalProps): JSX.Element => {
    return (
        <Modal open header="SummIT Guide™">
            <AdvancedFeatureUnavailableExplanation feature={getSummitGuideDueDiligenceFeature(props.module)} reason={props.reasonUnavailable} />
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};

interface ConfirmDueDiligenceAiAnalysisModalBaseProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentSubject: AssessmentSubjectBase | AssessmentSubjectArchiveBase;
    hideModal: () => void;
    module: DueDiligenceModule;
}

interface ConfirmDueDiligenceAiAssessmentAnalysisModalProps extends ConfirmDueDiligenceAiAnalysisModalBaseProps {
    type: 'Assessment';
    assessmentId: string;
    assessmentWideFiles: UploadedFile[];
    client: ClientDetailsModel;
    documentApi: DocumentApi;
    onAssessmentAnalysisRequested: (updatedAssessment: TprmOrUcrmDueDiligenceAssessment) => void;
}

interface ConfirmDueDiligenceAiFinalReviewAnalysisModalProps extends ConfirmDueDiligenceAiAnalysisModalBaseProps {
    type: 'FinalReview';
    allAssessmentsSubmitted: boolean;
    onFinalReviewAnalysisRequested: (updatedAssessmentSubject: AssessmentSubjectBase) => void;
}

/**
 * The analysis being requested is either for an individual assessment or for the final review of an assessment subject. `type` discriminates which kind of analysis this is.
 * The assessment variant lets the user choose which responses to include and sends an assessment analysis request; the final review variant has no such options and sends a body-less final review analysis request. Each request goes to the endpoint for `module`.
 */
export type ConfirmDueDiligenceAiAnalysisModalProps = ConfirmDueDiligenceAiAssessmentAnalysisModalProps | ConfirmDueDiligenceAiFinalReviewAnalysisModalProps;

/** This confirmation modal is used when the Client does have the module's SummIT Guide™ due diligence feature. It describes what will happen if the user confirms the action and lays out a disclaimer for the output. */
export const ConfirmDueDiligenceAiAnalysisModal = (props: ConfirmDueDiligenceAiAnalysisModalProps): JSX.Element => {
    const [formState, setFormState] = useState(FormState.None);
    const [includeClientResponses, setIncludeClientResponses] = useState<boolean>(false);
    const [includeQuestionnaireResponses, setIncludeQuestionnaireResponses] = useState<boolean>(true);

    const submit = async (): Promise<string> => {
        setFormState(FormState.Submitting);

        if (props.type === 'Assessment') {
            validateInput();

            const analysisRequest: AiDueDiligenceAnalyzeAssessmentRequest = {
                include_client_responses: includeClientResponses,
                include_third_party_responses: includeQuestionnaireResponses,
            };

            const startAssessmentAnalysis = props.module === ProductModule.UCRM ? props.artificialIntelligenceApi.aiUcrmUseCaseAssessmentStart : props.artificialIntelligenceApi.aiTprmServiceAssessmentStart;
            const updatedAssessment = (await startAssessmentAnalysis(analysisRequest, props.assessmentSubject.vendor_id, props.assessmentSubject.id, props.assessmentId)).data;
            props.onAssessmentAnalysisRequested(updatedAssessment);
        } else {
            const startFinalReviewAnalysis = props.module === ProductModule.UCRM ? props.artificialIntelligenceApi.aiUcrmFinalReviewStart : props.artificialIntelligenceApi.aiTprmFinalReviewStart;
            const updatedAssessmentSubject = (await startFinalReviewAnalysis(props.assessmentSubject.vendor_id, props.assessmentSubject.id)).data;
            props.onFinalReviewAnalysisRequested(updatedAssessmentSubject);
        }

        setFormState(FormState.Submitted);

        return 'SummIT Guide™ analysis has been started. Please refresh the webpage in a few minutes to view the resulting analysis.';
    };

    /** Validate the input before submitting to the API. Only applicable to assessment analysis, which lets the user choose what to include. */
    const validateInput = () => {
        if (props.type === 'Assessment' && includeClientResponses === false && includeQuestionnaireResponses === false && props.assessmentWideFiles.length === 0) {
            throw new ValidationError(`Neither ${props.module === ProductModule.UCRM ? 'respondent' : 'third-party'} responses nor internal responses were selected to be included, and there are no assessment-wide files to examine. At least one source of information must be provided for analysis.`);
        }
    };

    const disableForm = formState === FormState.Submitting || formState === FormState.Submitted;

    return (
        <ConfirmationModal buttonText="Start" buttonLoadingText="Starting..." buttonIcon={ICON_SUBMIT} operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE} headerText={props.type === 'Assessment' ? 'SummIT Guide™ Due Diligence Assessment Analysis' : 'SummIT Guide™ Final Review Analysis'} areYouSureText={props.type === 'Assessment' ? 'Are you sure you want SummIT Guide™ to start analyzing this assessment?' : 'Are you sure you want SummIT Guide™ to start analyzing this final review?'} performOperation={submit} hideModal={props.hideModal}>
            {props.type === 'Assessment' ? (
                <>
                    <Text>SummIT Guide™ will provide an automated analysis of responses to this assessment. Recommended control effectiveness ratings and corresponding explanations will be populated for each control in the assessment for your review. This may take several minutes to complete, depending on the number and complexity of responses.</Text>
                    <div className={styles.toggleButtonsContainer}>
                        <div className={styles.toggleButton}>
                            <Switch checked={includeQuestionnaireResponses} disabled={disableForm} onChange={() => setIncludeQuestionnaireResponses(!includeQuestionnaireResponses)} />
                            <Text noStyles>{props.module === ProductModule.UCRM ? 'Include respondent responses' : 'Include third-party responses'}</Text>
                            <FormFieldTooltip text={`Include answers to questions provided by ${props.module === ProductModule.UCRM ? 'the respondents of this use case' : props.assessmentSubject.vendor_name}. Unacceptable answers will always be taken into account, regardless of this selection.`} />
                        </div>
                        <div className={styles.toggleButton}>
                            <Switch checked={includeClientResponses} disabled={disableForm} onChange={() => setIncludeClientResponses(!includeClientResponses)} />
                            <Text noStyles>Include internal responses</Text>
                            <FormFieldTooltip text={`Include control assessments performed by users from ${props.client.client_name}.`} />
                        </div>
                    </div>
                </>
            ) : (
                <Text>SummIT Guide™ will provide an automated analysis to support this final review. A recommended overall control effectiveness and residual risk, along with supporting commentary, will be populated for your review. This may take several minutes to complete, depending on the number and complexity of responses.</Text>
            )}
            {props.type === 'FinalReview' && !props.allAssessmentsSubmitted && <Text color="red">Some assessments have not yet been submitted. It is recommended that all assessments be reviewed and submitted before analyzing the final review; otherwise, analysis may be based on incomplete information.</Text>}
            <br />
            <GenerativeAiDisclaimer />
        </ConfirmationModal>
    );
};
